import { Table, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow } from "@mui/material";
import { inject, observer } from "mobx-react";
import React, { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card } from "randmarcomps";
import ButtonWithActions from '../../components/util/ButtonWithActions';
import SalesLast30Days from "../../componentsCore/cards/SalesLast30Days";
import withManufacturer from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import Reseller from '../../stores/reseller/Reseller';
import { RootStore } from "../../stores/rootStore";
import ResellerActions from "../reseller/ResellerActions";

interface IProps {
  store?: RootStore;
  resellers?: Reseller[];
  applicationId: string;
  defaultRowsPerPage?: number;
}

const getPriorityClasses = (resellerPriority: string): string => {
  switch (resellerPriority) {
    case "Exclusive":
      return "bg-green-600 text-white";
    case "E-Commerce":
      return "bg-sky-500 text-white";
    case "Commercial":
      return "bg-amber-500 text-white";
    case "Academy":
      return "bg-lime-600 text-white";
    case "Novexco":
    case "Basics":
      return "bg-red-500 text-white";
    default:
      return "bg-[#582c83] text-white";
  }
};

const ResellersGrid: React.FC<IProps> = ({ store, resellers, applicationId, defaultRowsPerPage }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage || 50);

  const handleChangePage = useCallback((_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const { manufacturerStore: { manufacturer } } = store!;

  return (
    <Card className="w-full">
      <div className="w-full overflow-auto rounded-[20px]">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ borderColor: "#582c83" }} />
              <TableCell align="center" sx={{ borderColor: "#582c83" }}>Name</TableCell>
              <TableCell align="center" sx={{ borderColor: "#582c83" }}>Location</TableCell>
              <TableCell align="center" sx={{ borderColor: "#582c83" }}>Type</TableCell>
              <TableCell align="center" sx={{ borderColor: "#582c83" }}>Sales last 30 days</TableCell>
              <TableCell align="center" sx={{ borderColor: "#582c83" }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {(resellers || [])
              .slice()
              .map((row, index) => (
                <TableRow sx={{ "&:hover": { backgroundColor: "#f9f5ff" } }} key={"Receipt" + index}>
                  <TableCell align="center">
                    <img src={`https://api.randmar.io/V4/Partner/${row.resellerId || ""}/Account/Logo?width=75`} />
                  </TableCell>
                  <TableCell align="center">
                    <Link to={Path.reseller(applicationId, row.resellerId)}>{row.name}</Link>
                  </TableCell>
                  <TableCell align="center">{row.city}, {row.province}, {row.country}</TableCell>
                  <TableCell align="center">
                    <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityClasses(row.priority || "")}`}>
                      {row.priority}
                    </div>
                  </TableCell>
                  <TableCell align="center">
                    <SalesLast30Days salesData={row.salesData} fiscalYearMonthStart={manufacturer?.fiscalYearMonthStart} />
                  </TableCell>
                  <TableCell align="center">
                    <ButtonWithActions buttonText="Actions" backgroundColor="#f1f2f4" color="#363636">
                      <ResellerActions reseller={row} />
                    </ButtonWithActions>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
          {(resellers || []).length > rowsPerPage &&
            <TableFooter>
              <TableRow>
                <TablePagination
                  count={(resellers || []).length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableRow>
            </TableFooter>}
        </Table>
      </div>
    </Card>
  );
};

export default withManufacturer(inject("store")(observer(ResellersGrid)));
